import * as React from 'react';

import { Dispatch } from 'redux';
import { connect } from 'react-redux';

import AppState from '@webapp/store/state';

import styled from 'styled-components';

import { Translate } from 'react-localize-redux';

import { PaymentState } from '@webapp/store/payment/state';
import { CheckoutInitiated } from '@webapp/store/subscription/action-creators';
import { StripePaymentData } from '../../types/stripe/payment-data';

import Button from '@components/actions/button';
import Typography from '@components/basics/typography';

// import MyCardCtrl from '@webapp/controllers/payment/my-card';
import { SimplePaymentMethod } from '@services/payment/types/simple-payment-method';
import StripeCardEditor from './stripe-card-editor';
import NewMyCardCtrl from './my-card/new-card';
import { ButtonLoadingCtrl } from '@webapp/controllers/button-loading';

interface PaymentCtrlProps {
  className?: string;
  onSuccess: (paymentMethod: StripePaymentData) => void;
  onSavedCard: (card: SimplePaymentMethod) => void;
  onSelectCard?: (card?: SimplePaymentMethod) => void;
  onError: (value: boolean) => void;
  onStart: () => CheckoutInitiated;
  errorMsg: string;
  customBtnLabel?: React.ReactNode;
  noBtn?: boolean;
  noAbsoluteBtn?: boolean;
  isMobile: boolean;
  clientId?: string;
  cardList: PaymentState['methods'];
}

const mapStateToProps = (state: AppState) => ({
  isMobile: state.ui.mobile,
  cardList: state.payment.methods,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  onStart: () => dispatch(new CheckoutInitiated()),
});

export const PaymentCtrl: React.FC<PaymentCtrlProps> = (props) => {
  const { onStart, cardList, clientId, onSelectCard } = props;
  
  const [loading, setLoading] = React.useState(false);
  const [showAddCard, setShowAddCard] = React.useState(false);
  const [creditCardActive, setCreditCardActive] = React.useState<
    SimplePaymentMethod | undefined
  >(undefined);

  React.useEffect(() => {
    if (props.errorMsg) {
      setLoading(false);
    }
  }, [props.errorMsg]);

  React.useEffect(() => {
    if (onSelectCard) {
      onSelectCard(creditCardActive);
    }
    if (creditCardActive) {
      setShowAddCard(false);
    }
  }, [creditCardActive, onSelectCard]);

  const handleSavedCard = (card: SimplePaymentMethod) => {
    setLoading(true);
    return props.onSavedCard(card);
  };

  const handleToggleAddCard = () => {
    setShowAddCard(!showAddCard);
    if (!showAddCard) {
      setCreditCardActive(undefined);
      if (props.onSelectCard) {
        props.onSelectCard(undefined);
      }
    }
  };

  const handleNewCard = (data: StripePaymentData) => {
    props.onSuccess(data);
  };

  return (
    <div className={props.className}>
      {props.errorMsg ? (
        <ErrorTypography type="small" weight="bold" className="mb-20">
          {props.errorMsg}
        </ErrorTypography>
      ) : null}

      <Button
        buttonType="secondary-dark"
        onClick={handleToggleAddCard}
        fullWidth
        className={showAddCard ? 'mb-10' : ''}
      >
        <Translate id="PAYMENT.ADD_NEW.CARD" />
      </Button>

      {showAddCard && (
        <StripeCardEditor
          isFullWidth
          className="mb-20"
          onStart={onStart}
          onSuccess={handleNewCard}
          onError={props.onError}
          errorMsg={props.errorMsg}
          isMobile={props.isMobile}
          customBtnLabel={props.customBtnLabel}
          noBtn={props.noBtn}
          noAbsoluteBtn={props.noAbsoluteBtn}
        />
      )}

      {/* TODO: INTEGRATE NEW CREDIT CARDS */}
      {clientId && cardList[clientId]?.length ? (
        <CardsWrapper className="mt-10">
          {cardList[clientId].map((method) => (
            <NewMyCardCtrl
              key={method.id}
              creditCard={method}
              onSelection={setCreditCardActive}
              active={creditCardActive?.id === method.id}
            />
          ))}
        </CardsWrapper>
      ) : null}

      {creditCardActive && !showAddCard && !props.noAbsoluteBtn && (
        <MoveDown isFullWidth>
          <ButtonLoadingCtrl
            className="btn-loading"
            isLoading={loading}
            disabled={!creditCardActive}
            fullWidth
            onClick={() => handleSavedCard(creditCardActive)}
          >
            {loading ? (
              <Translate id="PAYMENT.PROCESSING" />
            ) : props.customBtnLabel ? (
              props.customBtnLabel
            ) : (
              <Translate id="PAYMENT.FINISH" />
            )}
          </ButtonLoadingCtrl>
        </MoveDown>
      )}
    </div>
  );
};

const MoveDown = styled.div<{ isFullWidth?: boolean }>`
  position: absolute;
  padding-top: 176px;
  text-align: center;
  width: ${({ isFullWidth }) => (isFullWidth ? '100%' : '27.5%')};
  min-width: 150px;
  cursor: pointer;
  ${({ theme }) => `@media screen and ${theme.device.laptopS}`} {
    padding-top: 190px;
  }

  .btn-loading {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(styled(PaymentCtrl)`
  flex-grow: 1;
  width: 100%;
  position: relative;

  .wrapper {
    display: flex;
    justify-content: space-around;
  }

  .card-number {
  }

  .card-expiry {
    min-width: 145px;
  }

  .card-cvv {
    max-width: 90px;
  }

  .card-zip {
    max-width: 120px;
  }

  .card-number,
  .card-expiry,
  .card-cvv {
    & > div {
      height: 16px;
    }
  }

  .StripeElement {
    min-height: 48px;
    transition: border-left-color
      ${({ theme }) =>
        `${theme.transitions.duration} ${theme.transitions.type}`};
    border-left: 3px solid;
    border-left-color: transparent;

    width: 100%;
    position: relative;
    border-radius: 2px;
    font-size: ${({ theme }) => theme.typography.h3};
    font-weight: 700;
    line-height: 16px;
    background-color: ${({ theme }) =>
      theme.colors.differentBackground} !important;
    color: ${({ theme }) => theme.colors.primary};
    cursor: auto;
    box-sizing: border-box;
    padding: 16px 12px 16px 9px;
    margin-right: 8px;
    margin-bottom: 8px;

    ${({ theme }) => `@media screen and ${theme.device.tablet}`} {
      font-size: ${({ theme }) => theme.typography.h4};
      min-height: 60px;
      padding: 22px 12px 22px 9px;
    }

    &:last-child {
      margin-right: 0;
    }

    &::placeholder,
    &::-webkit-input-placeholder {
      color: ${({ theme }) => theme.colors.primary};
      font-weight: 700;
      opacity: 1;
    }
  }

  .StripeElement--invalid {
    border-left-color: ${({ theme }) => theme.colors.error};
  }
`);

const ErrorTypography = styled(Typography)`
  color: ${({ theme }) => theme.colors.error};
`;

const CardsWrapper = styled.div`
  display: grid;
  grid-gap: 10px;
`;
